/**
 * Rubrica Emergenze (Variante A).
 * - Lettura: tutti gli utenti autenticati.
 * - Modifica contatti + Responsabile: solo super_admin.
 * - Warning modal preventivo: ogni accesso ricorda di avvisare il Responsabile.
 */
import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Phone, MessageCircle, AlertTriangle, Plus, Pencil, Trash2, UserCog, Loader2, Siren } from 'lucide-react';

const EmergencyContactsPage = () => {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'sede'].includes(user?.role);
  const [contacts, setContacts] = useState([]);
  const [responsible, setResponsible] = useState({ name: '', phone: '' });
  const [loading, setLoading] = useState(true);
  const [editDlg, setEditDlg] = useState(null);
  const [respDlg, setRespDlg] = useState(false);
  const [respForm, setRespForm] = useState({ name: '', phone: '' });
  const [acknowledged, setAcknowledged] = useState(false);
  const [warnOpen, setWarnOpen] = useState(true);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [c, r] = await Promise.all([api.get('/emergency/contacts'), api.get('/emergency/responsible')]);
      setContacts(c.data);
      setResponsible(r.data || { name: '', phone: '' });
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const blank = { name: '', category: 'Idraulico', phone: '', whatsapp: '', h24: false, schedule_note: '', notes: '', icon: '🔧', sort_order: 100 };

  const saveContact = async () => {
    if (!editDlg?.name || !editDlg?.phone || !editDlg?.category) return toast.error('Nome, categoria e telefono obbligatori');
    try {
      const isNew = !editDlg.id;
      const payload = { ...editDlg }; delete payload.id;
      if (isNew) await api.post('/emergency/contacts', payload);
      else await api.put(`/emergency/contacts/${editDlg.id}`, payload);
      toast.success(isNew ? 'Contatto aggiunto' : 'Contatto aggiornato');
      setEditDlg(null);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const removeContact = async (c) => {
    if (!window.confirm(`Eliminare "${c.name}"?`)) return;
    try { await api.delete(`/emergency/contacts/${c.id}`); toast.success('Eliminato'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const saveResponsible = async () => {
    if (!respForm.name || !respForm.phone) return toast.error('Nome e telefono obbligatori');
    try {
      await api.put('/emergency/responsible', respForm);
      toast.success('Responsabile aggiornato');
      setRespDlg(false);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const telHref = (n) => `tel:${(n || '').replace(/\s/g, '')}`;
  const waHref  = (n) => `https://wa.me/${(n || '').replace(/[^0-9]/g, '')}`;

  if (loading) return <div className="p-6 text-center text-slate-500">Caricamento...</div>;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="emergency-page">
      {/* Warning modal preventivo */}
      <Dialog open={warnOpen} onOpenChange={setWarnOpen}>
        <DialogContent className="max-w-md" data-testid="emergency-warn-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-6 h-6" /> Attenzione — accesso Emergenze
            </DialogTitle>
            <DialogDescription className="pt-2">
              Prima di chiamare un fornitore esterno o un numero di emergenza, <b>avvisa il Responsabile</b> e descrivi la situazione.
              Solo se non risponde, procedi.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-3 my-2">
            <div className="text-sm">👤 <b>Responsabile:</b> {responsible.name || <span className="text-slate-400">(non impostato)</span>}</div>
            {responsible.phone && (
              <div className="text-sm mt-1 flex items-center gap-2">
                📞 <a href={telHref(responsible.phone)} className="font-mono font-bold text-red-700">{responsible.phone}</a>
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} data-testid="emergency-ack" />
            Confermo di aver avvisato (o tentato) il Responsabile
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => window.history.back()}>Indietro</Button>
            <Button className="bg-red-600 hover:bg-red-700" disabled={!acknowledged} onClick={() => setWarnOpen(false)} data-testid="emergency-proceed">
              Ho capito, procedi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-red-700 flex items-center gap-2">
            <Siren className="w-7 h-7" /> Rubrica Emergenze
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Contatti H24 e numeri di assistenza straordinaria</p>
        </div>
        {isAdmin && (
          <Button className="bg-red-600 hover:bg-red-700" onClick={() => setEditDlg({ ...blank })} data-testid="add-contact-button">
            <Plus className="w-4 h-4 mr-2" /> Nuovo contatto
          </Button>
        )}
      </div>

      {/* Responsabile pinned */}
      <Card className="border-red-200 mb-5 dark:border-red-900" data-testid="responsible-card">
        <CardContent className="p-4 flex items-center gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-full bg-red-600 text-white flex items-center justify-center text-2xl font-black">
            {(responsible.name || '?').slice(0,1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-xs text-slate-500 uppercase tracking-wider">Responsabile</div>
            <div className="text-lg font-bold dark:text-slate-100">{responsible.name || <span className="text-slate-400">— non impostato —</span>}</div>
            <div className="text-sm text-slate-500">Avvisalo SEMPRE prima di chiamare un'emergenza</div>
          </div>
          {responsible.phone && (
            <a href={telHref(responsible.phone)} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-bold no-underline flex items-center gap-2" data-testid="responsible-call">
              <Phone className="w-4 h-4" /> {responsible.phone}
            </a>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={() => { setRespForm(responsible); setRespDlg(true); }} data-testid="edit-responsible">
              <UserCog className="w-4 h-4 mr-1" /> Modifica
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Contacts grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="contacts-grid">
        {contacts.length === 0 && (
          <div className="col-span-full text-center py-10 text-slate-500">Nessun contatto configurato. {isAdmin && 'Clicca "Nuovo contatto" per aggiungerne uno.'}</div>
        )}
        {contacts.map(c => (
          <Card key={c.id} className="dark:bg-slate-900" data-testid={`contact-card-${c.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3 mb-2">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0 ${c.h24 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                  {c.icon || '📞'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold dark:text-slate-100 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.category}</div>
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditDlg(c)} data-testid={`edit-contact-${c.id}`}><Pencil className="w-3.5 h-3.5"/></Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeContact(c)} data-testid={`del-contact-${c.id}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                )}
              </div>
              <div className="font-mono font-bold text-lg dark:text-slate-100">{c.phone}</div>
              <div className="flex gap-2 mt-1 flex-wrap">
                {c.h24 ? <Badge className="bg-red-600 text-white">H24</Badge> : <Badge className="bg-amber-500 text-white">{c.schedule_note || 'Feriali'}</Badge>}
                {c.whatsapp && <Badge className="bg-emerald-500 text-white">WhatsApp</Badge>}
              </div>
              {c.notes && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{c.notes}</p>}
              <div className="flex gap-2 mt-3">
                <a href={telHref(c.phone)} className="flex-1 bg-red-600 hover:bg-red-700 text-white text-center py-2 rounded-lg font-bold no-underline flex items-center justify-center gap-1" data-testid={`call-${c.id}`}>
                  <Phone className="w-3.5 h-3.5" /> Chiama
                </a>
                {c.whatsapp && (
                  <a href={waHref(c.whatsapp)} target="_blank" rel="noopener noreferrer" className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-2 rounded-lg no-underline flex items-center" data-testid={`wa-${c.id}`}>
                    <MessageCircle className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit / New contact dialog */}
      <Dialog open={!!editDlg} onOpenChange={(o) => !o && setEditDlg(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editDlg?.id ? 'Modifica contatto' : 'Nuovo contatto emergenza'}</DialogTitle></DialogHeader>
          {editDlg && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-[80px_1fr] gap-3">
                <div>
                  <Label>Icona</Label>
                  <Input value={editDlg.icon || ''} onChange={e => setEditDlg({ ...editDlg, icon: e.target.value })} placeholder="🔧" maxLength={3}/>
                </div>
                <div>
                  <Label>Nome / Fornitore *</Label>
                  <Input value={editDlg.name} onChange={e => setEditDlg({ ...editDlg, name: e.target.value })} data-testid="contact-name"/>
                </div>
              </div>
              <div>
                <Label>Categoria *</Label>
                <Input value={editDlg.category} onChange={e => setEditDlg({ ...editDlg, category: e.target.value })} placeholder="Idraulico / Elettricista / Ascensori..." data-testid="contact-category"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Telefono *</Label>
                  <Input value={editDlg.phone} onChange={e => setEditDlg({ ...editDlg, phone: e.target.value })} placeholder="+39 02 12345678" data-testid="contact-phone"/>
                </div>
                <div>
                  <Label>WhatsApp (opz.)</Label>
                  <Input value={editDlg.whatsapp || ''} onChange={e => setEditDlg({ ...editDlg, whatsapp: e.target.value })} placeholder="+39 333 1234567"/>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-950/30 rounded-lg">
                <Switch checked={editDlg.h24} onCheckedChange={(v) => setEditDlg({ ...editDlg, h24: v })} data-testid="contact-h24"/>
                <Label>Reperibile H24 (anche festivi)</Label>
              </div>
              {!editDlg.h24 && (
                <div>
                  <Label>Orario (es. "Feriali 8-18")</Label>
                  <Input value={editDlg.schedule_note || ''} onChange={e => setEditDlg({ ...editDlg, schedule_note: e.target.value })}/>
                </div>
              )}
              <div>
                <Label>Note operative</Label>
                <Input value={editDlg.notes || ''} onChange={e => setEditDlg({ ...editDlg, notes: e.target.value })} placeholder="contratto attivo, max 60min, zona Milano..."/>
              </div>
              <div>
                <Label>Ordine (minore = più in alto)</Label>
                <Input type="number" value={editDlg.sort_order || 100} onChange={e => setEditDlg({ ...editDlg, sort_order: Number(e.target.value) })}/>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDlg(null)}>Annulla</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={saveContact} data-testid="contact-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Responsible dialog */}
      <Dialog open={respDlg} onOpenChange={setRespDlg}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Modifica Responsabile</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Nome *</Label><Input value={respForm.name} onChange={e => setRespForm({ ...respForm, name: e.target.value })} data-testid="resp-name"/></div>
            <div><Label>Telefono *</Label><Input value={respForm.phone} onChange={e => setRespForm({ ...respForm, phone: e.target.value })} placeholder="+39 333 1234567" data-testid="resp-phone"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespDlg(false)}>Annulla</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={saveResponsible} data-testid="resp-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmergencyContactsPage;
